// Device-local roster of profiles that have signed in on this machine, powering the profile
// switcher and the lock-screen tiles. Descriptors ONLY — userId, loginId, displayName, hasPin,
// lastUsedAt. NEVER tokens or any other secret: every switch re-authenticates server-side
// (PIN or password) and mints a fresh JWT, so dormant profiles need nothing stored.
//
// Mirrors the pinned-messages-store pattern: module-level singleton + localStorage + a custom
// change event for subscribers. Device-global preferences (theme, sidebar, calendar filters)
// intentionally stay device-global for v1 — only the per-uid namespaced keys are purged when
// a profile is removed from the device.

import { destroyWorkspace } from "@/lib/stores/workspace-store";
import { removeFaceEnrollment } from "@/lib/face/face-enrollment-store";

const PROFILES_STORAGE_KEY = "healo-profiles";
const PROFILES_CHANGE_EVENT = "healo-profiles-change";

// Per-uid localStorage namespaces purged by removeProfile ("remove from this device").
const PINNED_MESSAGES_KEY_PREFIX = "healo-pinned-messages";
const SELECTED_THREAD_KEY_PREFIX = "healo-messages-selected-thread";
const WORKSPACE_KEY_PREFIX = "healo-workspace";
const USER_SETTINGS_CACHE_KEY_PREFIX = "healo-user-settings-cache";

let cache = null;

function canUseBrowserStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function nowIso() {
  return new Date().toISOString();
}

function normalizeProfile(value) {
  if (!value || typeof value !== "object") return null;
  const userId = String(value.userId || "").trim();
  const loginId = String(value.loginId || "").trim().toLowerCase();
  if (!userId || !loginId) return null;
  return {
    userId,
    loginId,
    displayName: String(value.displayName || "").trim() || loginId,
    hasPin: Boolean(value.hasPin),
    lastUsedAt: String(value.lastUsedAt || "").trim() || nowIso(),
  };
}

function normalizeProfileList(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const result = [];
  for (const entry of value) {
    const profile = normalizeProfile(entry);
    if (!profile) continue;
    if (seen.has(profile.userId)) continue;
    seen.add(profile.userId);
    result.push(profile);
  }
  return result;
}

function readFromStorage() {
  if (!canUseBrowserStorage()) return [];
  try {
    const raw = window.localStorage.getItem(PROFILES_STORAGE_KEY);
    if (!raw) return [];
    return normalizeProfileList(JSON.parse(raw));
  } catch {
    return [];
  }
}

function loadProfiles() {
  if (cache) return cache;
  cache = readFromStorage();
  return cache;
}

function persist(profiles) {
  if (!canUseBrowserStorage()) return;
  try {
    window.localStorage.setItem(PROFILES_STORAGE_KEY, JSON.stringify(profiles));
  } catch {
    /* ignore quota / serialization errors */
  }
}

function emitChange() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(PROFILES_CHANGE_EVENT));
}

function setProfiles(next) {
  cache = normalizeProfileList(next);
  persist(cache);
  emitChange();
  return cache;
}

// Most-recently-used first. ISO timestamps sort lexicographically by time.
function sortByLastUsedDesc(profiles) {
  return [...profiles].sort((a, b) => String(b.lastUsedAt).localeCompare(String(a.lastUsedAt)));
}

export function getProfiles() {
  return sortByLastUsedDesc(loadProfiles());
}

export function getProfileByUserId(userId) {
  const id = String(userId || "").trim();
  if (!id) return null;
  return loadProfiles().find((p) => p.userId === id) || null;
}

/**
 * Record (or refresh) a roster entry from a login-response / users-me user object.
 * Staff-only feature: non-employee users never join the roster (PIN switch is employee-gated
 * server-side; password login still works for them, it just leaves no tile behind).
 */
export function upsertProfileFromUser(user) {
  if (!user || typeof user !== "object" || !user.isEmployee) return getProfiles();
  const profile = normalizeProfile({
    userId: user.id,
    loginId: user.loginId,
    displayName: user.name || user.displayName || user.loginId,
    hasPin: Boolean(user.hasPin),
    lastUsedAt: nowIso(),
  });
  if (!profile) return getProfiles();
  const rest = loadProfiles().filter((p) => p.userId !== profile.userId);
  return setProfiles([profile, ...rest]);
}

export function setProfileHasPin(userId, hasPin) {
  const id = String(userId || "").trim();
  if (!id) return getProfiles();
  const profiles = loadProfiles();
  if (!profiles.some((p) => p.userId === id)) return getProfiles();
  return setProfiles(profiles.map((p) => (p.userId === id ? { ...p, hasPin: Boolean(hasPin) } : p)));
}

/**
 * Forget a profile on this device and purge its per-uid localStorage namespaces (settings
 * warm cache, remembered thread selections, legacy pinned/workspace keys) plus its face
 * enrollment. Server-side state is untouched — the user can always sign back in with full
 * credentials and their account settings return with them.
 */
export function removeProfile(userId) {
  const id = String(userId || "").trim();
  if (!id) return getProfiles();
  if (canUseBrowserStorage()) {
    try {
      const doomed = [];
      const pinnedKey = `${PINNED_MESSAGES_KEY_PREFIX}:${id}`;
      const selectedPrefix = `${SELECTED_THREAD_KEY_PREFIX}:${id}`;
      const workspaceKey = `${WORKSPACE_KEY_PREFIX}:${id}`;
      const settingsCacheKey = `${USER_SETTINGS_CACHE_KEY_PREFIX}:${id}`;
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const key = window.localStorage.key(i);
        if (!key) continue;
        // Selected-thread keys carry optional scope suffixes (":internal"), so prefix-match.
        if (
          key === pinnedKey ||
          key === workspaceKey ||
          key === settingsCacheKey ||
          key.startsWith(selectedPrefix)
        ) {
          doomed.push(key);
        }
      }
      for (const key of doomed) window.localStorage.removeItem(key);
    } catch {
      /* ignore storage access errors */
    }
  }
  // Remove-from-device also destroys the user's frozen workspace (memory + landing page).
  destroyWorkspace(id);
  // ...and their face enrollment, which carries a sealed PIN. Leaving that behind would keep a
  // replayable credential on a device the user just told us to forget them from.
  removeFaceEnrollment(id);
  return setProfiles(loadProfiles().filter((p) => p.userId !== id));
}

export function subscribeProfiles(listener) {
  if (typeof window === "undefined" || typeof listener !== "function") {
    return () => {};
  }
  const handler = () => listener(getProfiles());
  window.addEventListener(PROFILES_CHANGE_EVENT, handler);
  return () => window.removeEventListener(PROFILES_CHANGE_EVENT, handler);
}
